import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Images, Loader2, Plus, Video } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@orisini/ui/components/button'
import { Card, CardContent } from '@orisini/ui/components/card'
import { ApiError } from '@orisini/ui/lib/api'
import { cn } from '@orisini/ui/lib/utils'
import { postsApi, type PostDto } from '../api'
import { labelPost, mediaUtama } from '../label'

/**
 * Daftar post.
 *
 * Tabnya mengikuti PERJALANAN sebuah post, bukan enum statusnya mentah-mentah:
 * draf yang belum siap, yang sedang mengantre, dan yang sudah selesai. Delapan
 * status di database tidak berarti delapan tab yang berguna.
 */
const TAB = [
  { id: 'draft', label: 'Drafts', statuses: 'draft' },
  { id: 'antrean', label: 'Queue', statuses: 'queued,scheduled,dispatched,posting' },
  { id: 'selesai', label: 'Done', statuses: 'posted,failed,cancelled' },
] as const

type TabId = (typeof TAB)[number]['id']

export default function PostsPage() {
  const [tab, setTab] = useState<TabId>('antrean')
  const [posts, setPosts] = useState<PostDto[]>([])
  const [memuat, setMemuat] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    let batal = false
    setMemuat(true)
    const statuses = TAB.find((t) => t.id === tab)!.statuses
    void postsApi
      .list({ status: statuses, limit: 100 })
      .then((r) => {
        if (!batal) setPosts(r.posts)
      })
      .catch((e) => toast.error(e instanceof ApiError ? e.message : String(e)))
      .finally(() => {
        if (!batal) setMemuat(false)
      })
    return () => {
      batal = true
    }
  }, [tab])

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">Posts</h1>
        <Button onClick={() => navigate('/posts/baru')}>
          <Plus className="size-4" />
          New post
        </Button>
      </div>

      <div className="flex gap-1 border-b">
        {TAB.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={cn(
              '-mb-px border-b-2 px-3 py-2 text-sm',
              tab === t.id
                ? 'border-primary font-medium text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      {memuat ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </div>
      ) : posts.length === 0 ? (
        <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
          No posts in this tab yet.
        </p>
      ) : (
        <div className="space-y-2">
          {posts.map((p) => (
            <Link key={p.id} to={`/posts/${p.id}`} className="block">
              <Card className="transition-colors hover:border-primary/50">
                <CardContent className="flex items-center gap-3 p-3">
                  <div className="relative size-14 shrink-0 overflow-hidden rounded bg-muted">
                    {mediaUtama(p)?.previewUrl ? (
                      <img
                        src={mediaUtama(p)!.previewUrl!}
                        alt=""
                        className="size-full object-cover"
                        loading="lazy"
                      />
                    ) : null}
                    {/* Penanda jumlah slide & format: tanpa ini carousel dan
                        post tunggal tampak identik di daftar. */}
                    {p.slides.length > 1 && (
                      <span className="absolute right-0.5 top-0.5 rounded bg-black/70 px-1 text-[10px] text-white">
                        <Images className="inline size-3" /> {p.slides.length}
                      </span>
                    )}
                    {p.format === 'reel' && (
                      <span className="absolute bottom-0.5 left-0.5 rounded bg-black/70 px-1 text-[10px] text-white">
                        <Video className="inline size-3" />
                      </span>
                    )}
                  </div>

                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm">{labelPost(p)}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.status}
                      {p.scheduledAt
                        ? ` · ${new Date(p.scheduledAt).toLocaleString('en-GB')}`
                        : ''}
                      {p.problems.length ? ` · ${p.problems.length} not ready` : ''}
                    </p>
                    {p.lastErrorMessage && (
                      <p className="truncate text-xs text-destructive">{p.lastErrorMessage}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}
